// @ts-nocheck
/**
 * Preview — the bracket exactly as a parent or a coach sees it.
 *
 * The same line-and-border canvas the director edits on, with every control
 * taken away: no pencils, no dropdowns on the team lines, no ⋯ menu, no
 * Take off the board, no delete, no Add game, no Swap seeds. Nothing is
 * clickable except leaving. Read-only — it writes nothing.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { X, ExternalLink } from 'lucide-react';
import BracketView from '@/components/staff/tournaments/BracketView';

// Same body attribute the Full screen button sets — hides the app header and
// the sidebar. Reused rather than rewritten.
const FULLSCREEN_ATTR = 'data-scheduling-fullscreen';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
  versionId: string | null;
  division: string;
  bracketId: string;
  bracketName: string;
}

export default function BracketPreviewOverlay({
  open, onClose, eventId, versionId, division, bracketId, bracketName,
}: Props) {
  const [event, setEvent] = React.useState<any>(null);

  React.useEffect(() => {
    if (!open || !eventId) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from('events')
        .select('slug, name, published_schedule_version_id')
        .eq('id', eventId)
        .maybeSingle();
      if (!cancelled) setEvent(data ?? null);
    })();
    return () => { cancelled = true; };
  }, [open, eventId]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Cover the page the way Full screen does: header and sidebar hidden.
    document.body.setAttribute(FULLSCREEN_ATTR, 'true');
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      document.body.removeAttribute(FULLSCREEN_ATTR);
    };
  }, [open, onClose]);

  if (!open) return null;

  // Live means this exact version is the one the public page is serving.
  const isLive = !!event?.published_schedule_version_id &&
    (!versionId || event.published_schedule_version_id === versionId);
  const publicUrl = event?.slug
    ? `${window.location.origin}/events/${event.slug}`
    : null;
  const publicWords = event?.slug
    ? `${window.location.host}/events/${event.slug}`
    : '';

  // Rendered at the document root, not inside the scheduling pane: the pane's
  // own stacking would keep this under the app header and sidebar otherwise.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-background" data-bracket-preview>
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-bold tracking-tight">
            {division} · {bracketName}
          </h2>
          <div className="truncate text-xs text-muted-foreground" data-preview-published-line>
            {isLive ? (
              <>
                Live. This is what people see at{' '}
                {publicUrl ? (
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline underline-offset-2"
                  >
                    {publicWords}
                    <ExternalLink className="ml-1 inline h-3 w-3" />
                  </a>
                ) : 'the public event page'}
              </>
            ) : (
              'Not published. The public event page will show this once you publish the schedule.'
            )}
          </div>
        </div>
        <div className="ml-auto">
          <Button size="sm" onClick={onClose} title="Leave the preview (Esc)">
            <X className="mr-1.5 h-4 w-4" /> Exit preview
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6">
        <BracketView
          eventId={eventId}
          canManage={false}
          hideActions
          preview
          editable={false}
          versionId={versionId}
          division={division}
          bracketId={bracketId}
          bracketName={bracketName}
        />
      </div>
    </div>,
    document.body,
  );
}
